import { CustomPreloader, MMPagination } from '@components'
import cn from '@integrations/classnames'
import { toast } from '@integrations/notifications'
import { Fragment, type MouseEvent, useContext, useState } from '@integrations/react'
import styles from '@requests/scss/limits.module.scss'
import {
  CustomerRoles,
  selectCurrentUserRole,
  useApproveExpenseRequestMutation,
  useAppSelector,
  useCancelExpenseRequestMutation,
  useLazyFetchExpenseRequestsQuery,
  useMerchantAccounts,
  useRejectExpenseRequestMutation
} from 'mmfintech-backend-api'
import type { AccountBalanceResponse, ExpenseView } from 'mmfintech-backend-api/types'
import { GlobalContext, isValidArray, OtpContext, tr, usePagination } from 'mmfintech-commons'
import { MMTableView, NoElementsMessage } from 'mmfintech-portal-commons'
import RequestTableRow from '../RequestsTableRow'
import SuggestTransferModal from '../SuggestTransferModal'
import SortArrow from './SortArrow'

const { Table, Head, Row, HeadCell, Body } = MMTableView

const HEAD_CELLS = [
  { title: 'name', sort: 'name' },
  { title: 'description', sort: 'description' },
  { title: 'date', sort: 'createdOn' },
  { title: 'amount', sort: 'amount' },
  { title: 'recipient', sort: 'recipient', className: 'small' },
  { title: 'status', sort: 'pending' }
]

function RequestsTable() {
  const { modalShow } = useContext(GlobalContext)
  const { setOtpOnSuccess, setOtpOnError } = useContext(OtpContext)

  const customerRole = useAppSelector(selectCurrentUserRole)

  const [cancelRequest] = useCancelExpenseRequestMutation()
  const [rejectRequest] = useRejectExpenseRequestMutation()
  const [approveRequest] = useApproveExpenseRequestMutation()

  const { activeAccounts } = useMerchantAccounts()

  const [fetchExpenseRequests, { data: expenseRequests, isFetching }] = useLazyFetchExpenseRequestsQuery()

  const pagination = usePagination({
    rowsPerPage: 5,
    reload: async (params: any, onSuccess: (response: any) => void) => {
      try {
        const response = await fetchExpenseRequests({
          ...params,
          sort: params?.sort || 'pending-desc,requestId-desc'
        }).unwrap()

        onSuccess(response)
      } catch {}
    }
  })

  const [isSortAsc, setIsSortAsc] = useState(false)
  const [sort, setSort] = useState('pending-desc,requestId')

  function reloadWithSort(cusSort?: string) {
    return pagination.reload({
      size: 5,
      page: pagination?.currentPage,
      sort: `${cusSort || sort}-${isSortAsc ? 'desc' : 'asc'}`
    })
  }

  function reloadWithCurrentSort() {
    return pagination.reload({
      size: 5,
      page: pagination?.currentPage,
      sort: `${sort}-${isSortAsc ? 'asc' : 'desc'}`
    })
  }

  function handleSort(e: MouseEvent, sort: string) {
    e.stopPropagation()
    setSort(sort)
    reloadWithSort(sort)
    setIsSortAsc((prevState) => !prevState)
  }

  function handleReject(id: number) {
    if (customerRole === CustomerRoles.STAFF) {
      cancelRequest({ id: id }).unwrap().then(onSuccess).catch(onError)
    } else {
      rejectRequest({ id: id }).unwrap().then(onSuccess).catch(onError)
    }
  }

  function handleApprove(id: number, sourceAccountId: number) {
    setOtpOnSuccess(() => () => {
      reloadWithCurrentSort()
      toast.success(
        tr('FRONTEND.USERS.EXPENSE_MANAGEMENT.REQUESTS.TABLE.SUCCESS.MESSAGE', 'Request successfully approved')
      )
    })
    setOtpOnError(() => (error: any) => {
      if (error?.data?.status === 'MERCHANTS.ERROR.NOT_ENOUGH_BALANCE') {
        const account = isValidArray(activeAccounts)
          ? activeAccounts.find((a: AccountBalanceResponse) => a?.id === sourceAccountId)
          : null

        modalShow({
          options: {
            size: 'auto'
          },
          content: <SuggestTransferModal account={account} />
        })
      }
    })
    approveRequest({ id })
  }

  function onError(err: any) {
    toast.error(err?.error)
  }

  function onSuccess() {
    reloadWithCurrentSort()
    const message =
      customerRole === CustomerRoles.STAFF
        ? tr('FRONTEND.EXPENSE_MANAGEMENT.REQUESTS_CANCEL_MESSAGE', 'Request canceled')
        : tr('FRONTEND.EXPENSE_MANAGEMENT.REQUESTS_REJECT_MESSAGE', 'Request declined')
    toast.success(message)
  }

  return (
    <div
      className={cn(
        styles['limits-wrapper'],
        'limits-wrapper',
        'table-head-text-sm',
        'table-body-text-sm',
        'table-body-transparent'
      )}
    >
      {isFetching ? (
        <CustomPreloader />
      ) : isValidArray(expenseRequests?.content) ? (
        <>
          <Table skipHover>
            <Head>
              <Row className={cn('headRow')}>
                {HEAD_CELLS.map((cell) => {
                  return (
                    <Fragment key={cell.title}>
                      {!cell?.sort && !cell?.title ? <HeadCell /> : null}
                      {!cell?.sort && cell?.title ? (
                        <HeadCell className={cell?.className}>
                          {tr(`FRONTEND.EXPENSE_MANAGEMENT.REQUESTS.COLUMNS.${cell.title}`, `${cell.title}`)}
                        </HeadCell>
                      ) : null}
                      {cell?.sort && cell?.title ? (
                        <HeadCell
                          className={cn(sort === cell.sort && 'activeSortCell', cell?.className)}
                          onClick={(e: MouseEvent) => handleSort(e, cell.sort)}
                        >
                          <SortArrow headCellName={cell.sort} sort={sort} isSortAsc={isSortAsc} />
                          {tr(`FRONTEND.EXPENSE_MANAGEMENT.REQUESTS.COLUMNS.${cell.title}`, `${cell.title}`)}
                        </HeadCell>
                      ) : null}
                    </Fragment>
                  )
                })}
              </Row>
            </Head>

            <Body>
              {expenseRequests?.content.map((transaction: ExpenseView) => (
                <RequestTableRow
                  transaction={transaction}
                  key={transaction.requestId}
                  handleApprove={handleApprove}
                  handleReject={handleReject}
                />
              ))}
            </Body>
          </Table>

          <MMPagination
            className={cn('table-pagination')}
            {...pagination.register()}
            sort={`${sort}-${!isSortAsc ? 'desc' : 'asc'}`}
          />
        </>
      ) : (
        <NoElementsMessage message={tr('FRONTEND.EXPENSE_MANAGEMENT.NO_REQUESTS_MESSAGE', 'No expense requests yet')} />
      )}
    </div>
  )
}

export default RequestsTable
